import z from 'zod';
import {SVG} from '../../../../constants/assetLists';
import moment from 'moment';

export type QuestionType =
  | 'multiple-choice'
  | 'checkbox'
  | 'short-answer'
  | 'linear-scale'
  | 'rating'
  | 'date'
  | 'time'
  | 'dropdown'
  | 'file-upload'
  | 'range';

export type DateType = 'single' | 'range';
export type TimeType = 'single' | 'range';
export type TimingType = 'single' | 'scheduled';

// Mirrors backend RangeType enum
export type RangeType = 'numeric' | 'textual' | 'temperature';

// Mirrors backend RangeFormType enum (visual layout of the range)
export type RangeFormType = 'from-to' | 'between' | 'range';

export interface QuestionOption {
  id: string;
  title?: string;
  image?: File | string;
}

export interface BaseQuestion {
  id: string;
  _id?: string; // For existing questions coming from backend
  type: QuestionType;
  title: string;
  image?: File | string;
  mandatory: boolean;
}

export interface MultipleChoiceQuestion extends BaseQuestion {
  type: 'multiple-choice';
  options: QuestionOption[];
  hasOtherOption: boolean;
  otherOptionImage?: File | string;
}

export interface CheckboxQuestion extends BaseQuestion {
  type: 'checkbox';
  options: QuestionOption[];
  hasOtherOption: boolean;
  otherOptionImage?: File | string;
}

export interface DropdownQuestion extends BaseQuestion {
  type: 'dropdown';
  options: Omit<QuestionOption, 'image'>[];
}

export interface ShortAnswerQuestion extends BaseQuestion {
  type: 'short-answer';
  placeholder?: string;
}

export interface LinearScaleQuestion extends BaseQuestion {
  type: 'linear-scale';
  fromValue: number;
  toValue: number;
  fromLabel?: string;
  toLabel?: string;
}

export interface RatingQuestion extends BaseQuestion {
  type: 'rating';
  maxRating: number;
  icon: 'star' | 'heart' | 'smile' | 'glass';
}

export interface DateQuestion extends BaseQuestion {
  type: 'date';
  dateType: DateType;
}

export interface TimeQuestion extends BaseQuestion {
  type: 'time';
  timeType: TimeType;
}

export interface FileUploadQuestion extends BaseQuestion {
  type: 'file-upload';
  fileUploadConfig: {
    maxFileSizeMB: number;
    allowedTypes: string[];
    maxFiles: number;
  };
}

export interface RangeQuestion extends BaseQuestion {
  type: 'range';
  rangeConfig: {
    rangeType: RangeType;
    form: RangeFormType;
    min?: number;
    max?: number;
    minLabel?: string;
    maxLabel?: string;
    unit?: string;
    canUploadImage: boolean;
  };
}

export type Question =
  | MultipleChoiceQuestion
  | CheckboxQuestion
  | DropdownQuestion
  | ShortAnswerQuestion
  | LinearScaleQuestion
  | RatingQuestion
  | DateQuestion
  | TimeQuestion
  | FileUploadQuestion
  | RangeQuestion;

export interface CustomTimingSettings {
  repeatEvery: number;
  repeatType: string;
  weeklyDays: number[];
  monthlyDates: number[];
  endCondition: string;
  endDate?: string;
  occurrences?: number;
}

export interface FormSettings {
  allQuestionsRequired: boolean;
  countRespondents: boolean;
  anonymousForm: boolean;
  formForCustomer: boolean;
  branchIds: string[];
  subBranchIds: string[];
  departmentIds: string[] | 'all';
  recipientIds: string[];
  timingType: TimingType;
  schedulingType: string;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  time: string;
  dayOfWeek: number;
  customTiming: CustomTimingSettings | null;
  confirmationText: string;
}

export interface FormDataType {
  title: string;
  description: string;
  questions: Question[];
  settings: FormSettings;
  status: 'draft' | 'published';
}

export type FormTabType = 'questions' | 'settings' | 'responses';

// ---------- Response Types ----------

export interface FormResponse {
  respondedAt: string; // ISO date string
  responder?: {
    userId?: string;
    name?: string;
    email?: string;
  };
  value: any;
}

export interface QuestionResponseMetadata {
  // For rating questions
  maxRating?: number;
  ratingIcon?: 'star' | 'heart' | 'smile' | 'glass';
  // For linear-scale questions
  fromValue?: number;
  toValue?: number;
  fromLabel?: string;
  toLabel?: string;
  // For multiple-choice / checkbox — the list of all available options
  options?: string[];

  // question image
  qImage?: string;

  // For range questions
  qRange?: {
    rangeType: RangeType;
    form: RangeFormType;
    min?: number;
    max?: number;
    minLabel?: string;
    maxLabel?: string;
    unit?: string;
    canUploadImage: boolean;
  };
}

export interface QuestionResponse {
  questionId: string;
  questionTitle: string;
  questionType: QuestionType;
  metadata?: QuestionResponseMetadata;
  responses: FormResponse[];
}

export interface FormResponsesData {
  totalResponders: number;
  isPublicForm: boolean;
  questionsResponses: QuestionResponse[];
}

// ---------- Zod Schema ----------

const questionOptionSchema = z.object({
  id: z.string(),
  title: z.string().optional(),
  image: z.any().optional(),
});

const dropdownQuestionOptionSchema = questionOptionSchema.omit({image: true});

const baseQuestionSchema = z.object({
  id: z.string(),
  type: z.enum([
    'multiple-choice',
    'checkbox',
    'short-answer',
    'linear-scale',
    'rating',
    'date',
    'time',
    'dropdown',
    'file-upload',
    'range',
  ]),
  title: z.string().min(1, {message: 'שדה חובה!'}),
  image: z.any().optional(),
  mandatory: z.boolean(),
});

const multipleChoiceSchema = baseQuestionSchema.extend({
  type: z.literal('multiple-choice'),
  options: z.array(questionOptionSchema).min(2),
  hasOtherOption: z.boolean(),
  otherOptionImage: z.any().optional(),
});

const checkboxSchema = baseQuestionSchema.extend({
  type: z.literal('checkbox'),
  options: z.array(questionOptionSchema).min(2),
  hasOtherOption: z.boolean(),
  otherOptionImage: z.any().optional(),
});

const dropdownSchema = baseQuestionSchema.extend({
  type: z.literal('dropdown'),
  options: z.array(dropdownQuestionOptionSchema).min(2),
});

const shortAnswerSchema = baseQuestionSchema.extend({
  type: z.literal('short-answer'),
  placeholder: z.string().optional(),
});

const linearScaleSchema = baseQuestionSchema.extend({
  type: z.literal('linear-scale'),
  fromValue: z.number().min(1).max(10),
  toValue: z.number().min(1).max(10),
  fromLabel: z.string().optional(),
  toLabel: z.string().optional(),
});

const ratingSchema = baseQuestionSchema.extend({
  type: z.literal('rating'),
  maxRating: z.number().min(1).max(10),
  icon: z.enum(['star', 'heart', 'smile', 'glass']),
});

const dateSchema = baseQuestionSchema.extend({
  type: z.literal('date'),
  dateType: z.enum(['single', 'range']),
});

const timeSchema = baseQuestionSchema.extend({
  type: z.literal('time'),
  timeType: z.enum(['single', 'range']),
});

const fileUploadSchema = baseQuestionSchema.extend({
  type: z.literal('file-upload'),
  fileUploadConfig: z.object({
    maxFileSizeMB: z.number().min(1),
    allowedTypes: z
      .array(z.string())
      .min(1, {message: 'יש לבחור לפחות סוג קובץ אחד'}),
    maxFiles: z.number().min(1),
  }),
});

const rangeSchema = baseQuestionSchema.extend({
  type: z.literal('range'),
  rangeConfig: z
    .object({
      rangeType: z.enum(['numeric', 'textual', 'temperature']),
      form: z.enum(['from-to', 'between', 'range']),
      min: z.number().optional(),
      max: z.number().optional(),
      minLabel: z.string().optional(),
      maxLabel: z.string().optional(),
      unit: z.string().optional(),
      canUploadImage: z.boolean(),
    })
    .superRefine((cfg, ctx) => {
      if (cfg.rangeType === 'textual') return;
      const hasMin = cfg.min !== undefined && Number.isFinite(cfg.min);
      const hasMax = cfg.max !== undefined && Number.isFinite(cfg.max);
      if (hasMin && hasMax && cfg.max! <= cfg.min!) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'המקסימום חייב להיות גדול מהמינימום',
          path: ['max'],
        });
      }
    }),
});

const questionSchema = z.discriminatedUnion('type', [
  multipleChoiceSchema,
  checkboxSchema,
  dropdownSchema,
  shortAnswerSchema,
  linearScaleSchema,
  ratingSchema,
  dateSchema,
  timeSchema,
  fileUploadSchema,
  rangeSchema,
]);

const customTimingSchema = z
  .object({
    repeatEvery: z.number().min(1),
    repeatType: z.string(),
    weeklyDays: z.array(z.number()),
    monthlyDates: z.array(z.number()),
    endCondition: z.string(),
    endDate: z.string().optional(),
    occurrences: z.number().optional(),
  })
  .nullable();

const formSettingsSchema = z
  .object({
    allQuestionsRequired: z.boolean(),
    countRespondents: z.boolean(),
    anonymousForm: z.boolean(),
    formForCustomer: z.boolean(),
    branchIds: z.array(z.string()),
    subBranchIds: z.array(z.string()),
    departmentIds: z.union([z.array(z.string()), z.literal('all')]),
    recipientIds: z.array(z.string()),
    timingType: z.enum(['single', 'scheduled']),
    schedulingType: z.string(),
    startDate: z.string(),
    endDate: z.string(),
    startTime: z.string(),
    endTime: z.string(),
    time: z.string(),
    dayOfWeek: z.number(),
    customTiming: customTimingSchema,
    confirmationText: z.string(),
  })
  .superRefine((data, ctx) => {
    if (data.timingType !== 'scheduled') return;

    const requiresStartDate = [
      'default',
      'all_day',
      'by_day_and_time',
      'between_days_and_hours',
    ].includes(data.schedulingType);

    const requiresEndDate = ['default', 'between_days_and_hours'].includes(
      data.schedulingType,
    );

    const requiresStartEndTime = [
      'by_day_and_time',
      'between_days_and_hours',
      'daily_at_fixed_time',
      'custom',
    ].includes(data.schedulingType);

    if (requiresStartDate && (!data.startDate || !data.startDate.length)) {
      ctx.addIssue({
        code: 'custom',
        message:
          data.schedulingType === 'all_day'
            ? 'עבור תזמון לכל היום יש להגדיר תאריך'
            : 'יש להגדיר תאריך התחלה',
        path: ['startDate'],
      });
    }

    if (requiresEndDate && (!data.endDate || !data.endDate.length)) {
      ctx.addIssue({
        code: 'custom',
        message: 'יש להגדיר תאריך סיום',
        path: ['endDate'],
      });
    }

    if (requiresStartEndTime) {
      if (
        !data.startTime ||
        !data.startTime.length ||
        !moment(data.startTime, 'HH:mm', true).isValid()
      ) {
        ctx.addIssue({
          code: 'custom',
          message: 'יש להגדיר שעת התחלה',
          path: ['startTime'],
        });
      }
      if (
        !data.endTime ||
        !data.endTime.length ||
        !moment(data.endTime, 'HH:mm', true).isValid()
      ) {
        ctx.addIssue({
          code: 'custom',
          message: 'יש להגדיר שעת סיום',
          path: ['endTime'],
        });
      }
    }
  });

export const formDataSchema = z.object({
  title: z.string().min(1, {message: 'שדה חובה!'}),
  description: z.string(),
  questions: z.array(questionSchema).min(1),
  settings: formSettingsSchema,
  status: z.enum(['draft', 'published']),
});

export const QUESTION_TYPE_OPTIONS: {
  type: QuestionType;
  labelHe: string;
  labelEn: string;
  svgKey: string;
}[] = [
  {
    type: 'short-answer',
    labelHe: 'תשובה קצרה',
    labelEn: 'Short Answer',
    svgKey: 'shortAnswerTypeIcon',
  },
  {
    type: 'multiple-choice',
    labelHe: 'בחירה יחידה',
    labelEn: 'Multiple Choice',
    svgKey: 'multipleChoiceTypeIcon',
  },
  {
    type: 'checkbox',
    labelHe: 'תיבות סימון',
    labelEn: 'Checkbox',
    svgKey: 'checkboxTypeIcon',
  },
  {
    type: 'linear-scale',
    labelHe: 'סולם ליניארי',
    labelEn: 'Linear Scale',
    svgKey: 'linearScaleTypeIcon',
  },
  {
    type: 'rating',
    labelHe: 'דירוג',
    labelEn: 'Rating',
    svgKey: 'ratingTypeIcon',
  },
  {type: 'date', labelHe: 'תאריך', labelEn: 'Date', svgKey: 'dateTypeIcon'},
  {type: 'time', labelHe: 'שעה', labelEn: 'Time', svgKey: 'timeTypeIcon'},
  {
    type: 'dropdown',
    labelHe: 'תפריט נפתח',
    labelEn: 'Dropdown',
    svgKey: 'dropdownQuestionTypeIcon',
  },
  {
    type: 'file-upload',
    labelHe: 'העלאת קובץ',
    labelEn: 'File Upload',
    svgKey: 'uploadQuestionTypeIcon',
  },
  {
    type: 'range',
    labelHe: 'טווח',
    labelEn: 'Range',
    svgKey: 'rangeQuestionTypeIcon',
  },
];

export const RATING_ICONS = [
  {
    value: 'star',
    dropdownIcon: SVG.filledStarDropdownIcon,
    emptyIcon: SVG.emptyStarIcon,
  },
  {
    value: 'heart',
    dropdownIcon: SVG.filledHeartDropdownIcon,
    emptyIcon: SVG.emptyHeartIcon,
  },
  {
    value: 'smile',
    dropdownIcon: SVG.filledSmileDropdownIcon,
    emptyIcon: SVG.emptySmileIcon,
  },
  {
    value: 'glass',
    dropdownIcon: SVG.filledGlassDropdownIcon,
    emptyIcon: SVG.emptyGlassIcon,
  },
];

export const DEFAULT_FORM_SETTINGS: FormSettings = {
  allQuestionsRequired: false,
  countRespondents: false,
  anonymousForm: false,
  formForCustomer: false,
  branchIds: [],
  subBranchIds: [],
  departmentIds: [],
  recipientIds: [],
  timingType: 'single',
  schedulingType: 'default',
  startDate: '',
  endDate: '',
  startTime: '',
  endTime: '',
  time: '',
  dayOfWeek: 0,
  customTiming: null,
  confirmationText: '',
};

export const createDefaultQuestion = (
  type: QuestionType,
  id: string,
  question?: any,
  isMandatory?: boolean,
): Question => {
  const baseQuestion = {
    id,
    title: question?.title || '',
    image: question?.image,
    mandatory: question?.required || isMandatory || false,
  };
  const otherOption = question?.options?.find((opt: any) => opt.isOther);
  const questionOptions = question?.options
    ?.filter((op: {isOther: boolean}) => op?.isOther === false)
    ?.map((opt: any) => ({
      id: opt._id,
      title: opt.label || '',
      image: opt.image,
    }));
  const dropdownQuestionOptions = question?.options
    ?.filter((op: {isOther?: boolean}) => !op?.isOther)
    ?.map((opt: any) => ({
      id: opt._id,
      title: opt.label || '',
    }));
  switch (type) {
    case 'multiple-choice':
      return {
        ...baseQuestion,
        type: 'multiple-choice',
        options: questionOptions || [
          {id: `${id}-opt-1`, title: '', image: undefined},
          {id: `${id}-opt-2`, title: '', image: undefined},
        ],
        hasOtherOption: otherOption?.isOther || false,
        otherOptionImage: otherOption?.image || undefined,
      };
    case 'checkbox':
      return {
        ...baseQuestion,
        type: 'checkbox',
        options: questionOptions || [
          {id: `${id}-opt-1`, title: '', image: undefined},
          {id: `${id}-opt-2`, title: '', image: undefined},
        ],
        hasOtherOption: otherOption?.isOther || false,
        otherOptionImage: otherOption?.image || undefined,
      };
    case 'dropdown':
      return {
        ...baseQuestion,
        type: 'dropdown',
        options: dropdownQuestionOptions || [
          {id: `${id}-opt-1`, title: ''},
          {id: `${id}-opt-2`, title: ''},
        ],
      };
    case 'short-answer':
      return {
        ...baseQuestion,
        type: 'short-answer',
      };
    case 'linear-scale':
      return {
        ...baseQuestion,
        type: 'linear-scale',
        fromValue: question?.linearScale?.min || 1,
        toValue: question?.linearScale?.max || 5,
        fromLabel: question?.linearScale?.minLabel || '',
        toLabel: question?.linearScale?.maxLabel || '',
      };
    case 'rating':
      return {
        ...baseQuestion,
        type: 'rating',
        maxRating: question?.rating?.max || 5,
        icon: question?.rating?.icon || 'star',
      };
    case 'date':
      return {
        ...baseQuestion,
        type: 'date',
        dateType: question?.dateConfig?.range ? 'range' : 'single',
      };
    case 'time':
      return {
        ...baseQuestion,
        type: 'time',
        timeType: question?.timeConfig?.range ? 'range' : 'single',
      };
    case 'file-upload':
      return {
        ...baseQuestion,
        type: 'file-upload',
        fileUploadConfig: {
          maxFileSizeMB: question?.fileUploadConfig?.maxFileSizeMB ?? 10,
          allowedTypes: [...(question?.fileUploadConfig?.allowedTypes ?? [])],
          maxFiles: question?.fileUploadConfig?.maxFiles ?? 1,
        },
      };
    case 'range': {
      return {
        ...baseQuestion,
        type: 'range',
        rangeConfig: {
          rangeType: question?.rangeConfig?.rangeType ?? 'numeric',
          form: question?.rangeConfig?.form ?? 'between',
          min: question?.rangeConfig?.min ?? 0,
          max: question?.rangeConfig?.max ?? 100,
          minLabel: question?.rangeConfig?.minLabel ?? '',
          maxLabel: question?.rangeConfig?.maxLabel ?? '',
          unit: question?.rangeConfig?.unit ?? '',
          canUploadImage: question?.rangeConfig?.canUploadImage ?? false,
        },
      };
    }
  }
};
